/**
 * „Do przejrzenia" — an inbox: the queue on the left, the open submission on the right.
 *
 * ── Master-detail since 4 November 2026 ──────────────────────────────────────────────────────
 * Owner („to fajne"): the list-plus-modal became one card. The queue's header counts what waits
 * and names the keys, the pane carries „Przejrzane, dalej", and J / K / E walk the queue without a
 * pointer (`lib/useReviewTab`, `model/reviewInbox`). No read was added: the queue is the review
 * queue's, the pane is the client's own survey history the client detail reads.
 *
 * ── The window is SAID, from the response's own field ────────────────────────────────────────
 * „Ostatnie 30 dni" under the pane, and the sentence that reviewed submissions stay in the client's
 * history: a trainer looking at an empty inbox has to know whether nothing waits or older things
 * have left a window. The figure is the backend's, never a constant here.
 *
 * ── Nothing moves when the queue lands ───────────────────────────────────────────────────────
 * The card fills the tab's height in every state; the list and the pane are its two columns, each
 * with its own skeleton inside the box it will fill, and the foot is a fixed band. `pending`,
 * `error` and `empty` are three states of the card's inside, never a branch on the row count.
 */

import { useTranslation } from 'react-i18next';

import { useScreenMotionPreference } from '~/shared/design-system/motion';
import { useAnnouncer } from '~/shared/lib/useAnnouncer';
import { EmptyState } from '~/shared/ui/EmptyState';
import { DoneMarkIcon, NoteMarkIcon } from '~/shared/ui/EmptyStateIcons';
import { LiveRegionAnnouncer } from '~/shared/ui/LiveRegionAnnouncer';
import ghost from '~/shared/ui/ghostButton.module.css';
import kbd from '~/shared/ui/kbd.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import stateAction from '~/shared/ui/stateAction.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { useReviewTab } from '../lib/useReviewTab';
import { ReviewPane } from './ReviewPane';
import { ReviewQueueList } from './ReviewQueueList';
import styles from './ReviewQueue.module.css';

/** Five rows, what `--h-report-queue-min` was derived from. */
const SKELETON_ROWS = 5;

function InboxSkeleton({ skeleton }: { readonly skeleton: boolean }) {
  const { t } = useTranslation();

  return (
    <div className={styles.inbox} aria-busy="true">
      <div className={styles.listColumn}>
        <div className={styles.listHead} />
        <div className={styles.list}>
          <p className={hidden.srOnly}>{t('web:reports.queue.states.loading')}</p>
          {Array.from({ length: SKELETON_ROWS }, (_, row) => (
            <div key={row} className={styles.skelRow}>
              {skeleton ? (
                <>
                  <i className={`${String(skel.bar)} ${String(styles.skelFace)}`} />
                  <span className={styles.skelText}>
                    <i className={`${String(skel.bar)} ${String(styles.skelName)}`} />
                    <i className={`${String(skel.bar)} ${String(styles.skelSummary)}`} />
                  </span>
                  <i className={`${String(skel.bar)} ${String(styles.skelWhen)}`} />
                </>
              ) : null}
            </div>
          ))}
        </div>
      </div>
      <div className={styles.pane}>
        <div className={styles.paneHead}>
          {skeleton ? <i className={`${String(skel.bar)} ${String(styles.skelTitle)}`} /> : null}
        </div>
        <div className={styles.paneBody}>
          <div className={styles.paneReserve}>
            {skeleton
              ? [0, 1, 2].map((block) => (
                  <i key={block} className={`${String(skel.bar)} ${String(styles.skelBlock)}`} />
                ))
              : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ReviewQueueTab() {
  const { t } = useTranslation();
  const announcer = useAnnouncer();
  const reducedMotion = useScreenMotionPreference();
  const review = useReviewTab(announcer.announce);

  return (
    <div className={styles.tab}>
      <div className={styles.card}>
        {review.listState === 'pending' ? (
          <InboxSkeleton skeleton={review.listSkeleton} />
        ) : review.listState === 'rows' && review.opened !== null ? (
          <div className={styles.inbox}>
            <div className={styles.listColumn}>
              <div className={`${String(styles.listHead)} ${review.listReveal}`}>
                <span className={styles.listCount}>
                  {t('web:reports.queue.waiting', { count: review.queue.rows.length })}
                </span>
                <span className={styles.listKeys} title={t('web:reports.queue.keysHintLabel')}>
                  <span aria-hidden>{t('web:reports.queue.keysHint')}</span>
                  <kbd className={`${String(kbd.kbd)} ${String(styles.listKbd)}`} aria-hidden>
                    J
                  </kbd>
                  <kbd className={`${String(kbd.kbd)} ${String(styles.listKbd)}`} aria-hidden>
                    K
                  </kbd>
                  <span className={hidden.srOnly}>{t('web:reports.queue.keysHintLabel')}</span>
                </span>
              </div>
              <ReviewQueueList
                rows={review.queue.rows}
                openedId={review.opened.assignmentId}
                onOpen={review.onOpen}
                reveal={review.listReveal}
              />
            </div>
            <ReviewPane
              row={review.opened}
              previousId={review.previousId}
              nextId={review.nextId}
              onOpen={review.onOpen}
              onReviewNext={review.onReviewNext}
              marking={review.marking}
              reducedMotion={reducedMotion}
            />
          </div>
        ) : (
          <div className={styles.state}>
            {review.listState === 'error' ? (
              <>
                <EmptyState
                  mark={<NoteMarkIcon />}
                  tone="neutral"
                  title={t('web:reports.queue.states.errorTitle')}
                  hint={t('web:reports.queue.states.errorMessage')}
                />
                <button
                  type="button"
                  className={`${String(ghost.ghost)} ${String(stateAction.stateAction)}`}
                  onClick={review.onRetry}
                >
                  {t('web:reports.queue.states.retry')}
                </button>
              </>
            ) : (
              // Nothing waiting is GOOD NEWS for a trainer and the tone says so.
              <EmptyState
                mark={<DoneMarkIcon />}
                tone="positive"
                title={t('web:reports.queue.empty.title')}
                hint={t('web:reports.queue.empty.message')}
              />
            )}
          </div>
        )}

        <div className={styles.foot}>
          {review.markFailed ? (
            <span className={styles.failed} role="alert">
              {t('web:reports.queue.markFailed')}
            </span>
          ) : (
            <span>
              {t('web:reports.queue.reviewedNote')}
              {review.queue.truncated ? ` ${t('web:reports.queue.truncated')}` : null}
            </span>
          )}
          <span>{review.windowNote}</span>
        </div>
      </div>

      <LiveRegionAnnouncer message={announcer.message} />
    </div>
  );
}
